import React, { useState } from 'react';
import { ArrowRight, Menu, X } from 'lucide-react';
import './DesignerHeader.mobile.css';
import { useTranslation } from 'react-i18next';
import { useDesignStore } from '../../store/designStore';
import { crossOriginForUrl } from '../../lib/crossOriginImage';
import { notifyParent } from '../../hooks/useDesignerInit';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { HISTORY_SNAPSHOT_PROPS } from '../../store/historyStore';
import { buildDownloadPdfDataUrls, buildTemplateThumbnailDataUrl } from '../../lib/designDownload';
import { safeGetItem } from '../../lib/safeStorage';
import { decodeJwtRole } from '../../lib/jwt';
import { useToast } from '../Notifications';
import { ProductOptionsModal } from '../ProductOptionsModal';
import { FULL_COLOR, capitalizeLabel, type PrintColorOption } from '@customedge/shared';
import { translatedMethodLabel, translatedPositionLabel, translatedColorOptionLabel } from '../../lib/translatedLabels';
import { useFinishDesign } from '../../hooks/useFinishDesign';

interface DesignerHeaderProps {
  isPopupMode?: boolean;
  isAdminMode?: boolean;
  brandLogoUrl?: string | null;
  brandLogoLink?: string | null;
  onCartClick?: () => void;
  activeTab: 'design' | 'preview' | '3d';
  onTabChange: (tab: 'design' | 'preview' | '3d') => void;
}

export function DesignerHeader({ isPopupMode, isAdminMode, brandLogoUrl, brandLogoLink, onCartClick, activeTab, onTabChange }: DesignerHeaderProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const {
    productConfig, canvasInstances, activeSideKey, savedDesignId, setSavedDesignId, sides, getCustomizePrice,
    nameNumbers, vdpData, sideColorCountJsonCache, resolveActiveColorCountKey, layoutConfig, selectedVariantOptions,
    colorVariants, activeColorVariantId, hasSelectedColorVariant, selectedPrintMethod, selectedPrintColorOption,
    selectedPrintPositions, selectedPrintMethodByPosition, selectedPrintColorByPosition, bumpSavedDesignVersion,
    clearDirtySideKeys,
  } = useDesignStore();

  // `isAdminMode` alone is just the `?admin=1` URL flag — a regular customer can
  // append that to the URL too. The backend's `authenticateAdmin` middleware is
  // what actually blocks them (their token, if any, won't have `role:'admin'`),
  // but showing them a button that's guaranteed to fail on click is still bad UX
  // and looks like a bug, not a permission boundary. Only render it when the
  // session's own token really does decode to an admin role.
  const isStaffAdmin = isAdminMode && decodeJwtRole(safeGetItem('session', 'customedge_token')) === 'admin';

  // On the Preview tab, "Next" becomes "Finish Design" in place — same button,
  // same finalize action PreviewView's toggle-row button used to own (see
  // useFinishDesign.ts) — rather than a second, separately-rendered button.
  const { finishDesign, loading: finishLoading, error: finishError, done: finishDone } = useFinishDesign(!!isPopupMode);
  const isFinishStep = activeTab === 'preview';

  const designLabel  = t('designer.tabs.design');
  const previewLabel = t('designer.tabs.preview');
  // 3D Preview only makes sense when a GLB model is uploaded AND the layout
  // has it enabled AND the per-product "Display 3D" admin flag is on (an
  // admin can upload/calibrate a model without exposing it yet, e.g. while
  // its UV wrapping still looks wrong). Preview tab is also layout-gated.
  const has3dModel = !!productConfig?.glbModelUrl && productConfig?.display3dModel !== false;
  const showPreview = layoutConfig.previewTabEnabled !== false;
  const show3d = has3dModel && layoutConfig.preview3dTabEnabled !== false;
  const tabs: Array<'design' | 'preview' | '3d'> = [
    'design',
    ...(showPreview ? ['preview' as const] : []),
    ...(show3d ? ['3d' as const] : []),
  ];

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showMobileDownloadModal, setShowMobileDownloadModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [hoveredSelectorKey, setHoveredSelectorKey] = useState<string | null>(null);
  const [templateTitle, setTemplateTitle] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  // Tracks whether the merchant-supplied logo <img> failed to load (bad URL,
  // 404, network hiccup) — falls back to the "CustomEdge" text mark instead
  // of leaving a blank gap at the header's left edge.
  const [logoFailed, setLogoFailed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Shared by handleSave and handleShare — both need the same POST /api/designs
  // call (existing design_id if one is already saved, so repeat calls update
  // the same row rather than creating a new one each time) and both need the
  // resulting designId back to act on. Doesn't touch saving/saveError state or
  // the popup-mode DESIGNER_SAVED postMessage — those stay in handleSave, since
  // a background share-triggered save shouldn't flip the header into "Saving..."
  // or tell the embedding host page a save happened.
  const saveCurrentDesign = async (): Promise<{ designId: string; previewImageUrl: string; widgetUrl: string }> => {
    if (!productConfig?.productId) throw new Error(t('designer.error.missingConfig'));

    // Only the active side has a live Fabric canvas in `canvasInstances` —
    // DesignerWorkspace reuses one <canvas> across sides, disposing the
    // previous one on every switch. `sideColorCountJsonCache` (populated by
    // useFabricCanvas's cleanup, keyed by sideKey THEN by print-color-count —
    // see designStore.ts's field comment) is what makes every other already-
    // edited side/count's content actually survive into this save.
    const canvasMap: Record<string, Record<string, any>> = structuredClone(sideColorCountJsonCache);
    for (const side of sides) {
      const canvas = canvasInstances.get(side.sideKey);
      if (canvas) {
        const countKey = resolveActiveColorCountKey(side);
        canvasMap[side.sideKey] = {
          ...canvasMap[side.sideKey],
          [countKey]: canvas.toJSON(HISTORY_SNAPSHOT_PROPS),
        };
      }
    }

    const canvasJson = btoa(JSON.stringify(canvasMap));
    const token = safeGetItem('session', 'customedge_token');

    // Preview of what the customer actually configured — a 3D screenshot with
    // their selected Object Variants baked in when the product has a GLB, else
    // the 2D product-photo composite. The backend persists it as the design's
    // 'base' image (cart/order thumbnail, admin drawer, approval fallback).
    const { buildDesignPreviewDataUrl } = await import('../../lib/designPreviewCapture');
    const previewImageDataUrl = await buildDesignPreviewDataUrl().catch(() => null);

    const res = await fetch('/api/designs', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        product_id:         productConfig.productId,
        // "My Design" retention key for guests (see useDesignerInit.ts's
        // `hash` handling) — the backend only reads this when there's no JWT
        // (`req.user` absent), storing it on Design.sessionId so a guest with
        // a live Magento session can list/reload their own saves in
        // MyDesignsTool without an account. Sent unconditionally (even when a
        // JWT is also present) since the save route ignores it whenever
        // `req.user` is set — same "JWT wins" precedence as authHeaders().
        sessionId:          safeGetItem('session', 'customedge_design_hash') ?? undefined,
        canvasJson,
        previewJson:        canvasJson,
        imageWidth:         sides[0]?.canvasWidth  ?? 400,
        imageHeight:        sides[0]?.canvasHeight ?? 400,
        design_id:          savedDesignId ?? undefined,
        customizePrices:    getCustomizePrice(),
        name_number:        nameNumbers.length ? JSON.stringify(nameNumbers) : undefined,
        vdp_data:           vdpData.length ? JSON.stringify(vdpData) : undefined,
        // Popup-selected print method / print color (ProductOptionsModal) —
        // replaces the dead `printingMethod` store field (never set by any
        // UI, see IMPLEMENTATION-STATUS.md's printing-method Gap Priority
        // Queue row) with this feature's own real selections. No new Design
        // column: printingMethodData already stores whatever's passed as
        // JSON, so both selections travel together in one object.
        // `positions`/`methodByPosition`/`colorByPosition` carry the
        // multiselect print-position feature's own per-position resolution
        // (e.g. Zeefdruk+2kleuren on voorzijde AND DTF Transfer+max500cm² on
        // achterzijde simultaneously) — additive fields, all undefined for
        // any product with no positions configured, so this stays
        // byte-identical to before this feature existed for every
        // pre-existing saved design/consumer.
        printingMethodData: (selectedPrintMethod || selectedPrintColorOption != null || selectedPrintPositions.length > 0)
          ? {
              method: selectedPrintMethod ?? undefined,
              printColorOption: selectedPrintColorOption ?? undefined,
              positions: selectedPrintPositions.length ? selectedPrintPositions : undefined,
              methodByPosition: Object.keys(selectedPrintMethodByPosition).length ? selectedPrintMethodByPosition : undefined,
              colorByPosition: Object.keys(selectedPrintColorByPosition).length ? selectedPrintColorByPosition : undefined,
            }
          : undefined,
        selectedVariantOptions: Object.keys(selectedVariantOptions).length ? selectedVariantOptions : undefined,
        // The customer's chosen vendor Product Color Variant (ProductOptionsModal's
        // "Product color" pill, or `?colorVariant=` at launch) — without sending
        // this, no consumer that reloads the saved design (widget preview,
        // approval page, admin PDF/PNG/JPG/PSD download) can tell which color
        // was actually picked, and silently falls back to the first color
        // variant on every reload. Only sent when the customer made an actual
        // selection (`hasSelectedColorVariant`), not the auto-picked-first-
        // variant default `activeColorVariantId` already holds before any
        // explicit choice.
        colorVariantId: hasSelectedColorVariant ? (activeColorVariantId ?? undefined) : undefined,
        previewImageDataUrl: previewImageDataUrl ?? undefined,
        source:             'save',
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }

    const data = await res.json();
    if (!data.designId) throw new Error(t('designer.error.saveFailed'));
    setSavedDesignId(data.designId);
    // REAL BUG found+fixed 2026-08-21 (live user report: editing text then
    // clicking Save/Next again — AFTER the Preview tab had already shown a
    // finished render once — left the Preview tab permanently stuck on the
    // OLD image). saveCurrentDesign() is the single choke point BOTH the
    // Save button (handleSave) and the Next/Finish flow route through, so
    // bumping here covers every manual-save entry point in one place — see
    // designStore.ts's savedDesignVersion comment for the full mechanism
    // this closes.
    bumpSavedDesignVersion();
    // This save sends the FULL current canvasMap for every side with
    // content — see designStore.ts's dirtySideKeys comment for why a
    // successful save here means every side is current, not just whichever
    // one(s) were actually edited.
    clearDirtySideKeys();
    // Relative path from the backend — absolutize with our own origin, same as
    // buildShareUrl below (the backend doesn't track a public frontend URL).
    const widgetUrl = data.widgetUrl ? `${window.location.origin}${data.widgetUrl}` : '';
    return { designId: data.designId, previewImageUrl: data.previewImageUrl ?? '', widgetUrl };
  };

  const handleSave = async () => {
    if (!productConfig?.productId) {
      setSaveError(t('designer.error.missingConfig'));
      return;
    }
    setSaving(true);
    setSaveError(null);

    try {
      // saveCurrentDesign already captured and persisted the preview image
      // (3D selected-variant screenshot, or the 2D composite) server-side as
      // the design's 'base' image — the returned URL is what the embedding
      // host page (Magento cart) needs, so no separate /api/preview round
      // trip anymore.
      const { designId, previewImageUrl, widgetUrl } = await saveCurrentDesign();
      if (designId && isPopupMode) {
        notifyParent('DESIGNER_SAVED', { designId, previewUrl: previewImageUrl, widgetUrl, price: 0 });
      }
    } catch (err: any) {
      setSaveError(err.message ?? t('designer.error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Admin-only: captures the live canvas and posts it to the generic admin asset
  // CRUD route as a new `DesignTemplate` row — replaces hand-typing base64 Fabric
  // JSON into a textarea on the admin Templates page. Deliberately writes the
  // SAME flat, one-snapshot-per-side shape `handleSave` used before the
  // per-print-color-count feature existed, NOT that feature's newer nested
  // shape — a template isn't tied to any particular Print Color count, it's a
  // one-shot content snapshot meant to apply fresh onto whatever count is
  // active on whichever product it's later applied to. `loadFromTemplate`'s
  // own back-compat shim already attributes a flat entry to the CURRENTLY
  // active count on read, which is exactly this semantic — see its comment in
  // designStore.ts. Thumbnail is design-only (transparent background, no
  // product/garment photo) — a template never carries that photo (TemplateTool's
  // applyTemplate only ever restores the design objects), so baking it into the
  // thumbnail would wrongly suggest picking the template also changes the product
  // or its color.
  const handleSaveAsTemplate = async () => {
    if (!productConfig?.productId) {
      setTemplateError(t('designer.error.missingConfig'));
      return;
    }
    if (!templateTitle.trim()) {
      setTemplateError(t('designer.error.templateTitleRequired'));
      return;
    }

    setSavingTemplate(true);
    setTemplateError(null);

    try {
      // A template is a one-shot content snapshot, not tied to any particular
      // Print Color count — it gets applied fresh via loadFromTemplate onto
      // whatever count the customer viewing it later has active (that
      // function's own back-compat shim already attributes a flat legacy
      // entry to the CURRENTLY active count on read, which is exactly the
      // "applies on top of current context" semantic a template needs). So
      // this deliberately flattens down to ONE snapshot per side — whichever
      // print-color count is active for that side right now — rather than
      // writing the newer per-count nested shape `Design.jsonObjects` uses.
      const canvasMap: Record<string, any> = {};
      for (const side of sides) {
        const canvas = canvasInstances.get(side.sideKey);
        if (canvas) {
          canvasMap[side.sideKey] = canvas.toJSON(HISTORY_SNAPSHOT_PROPS);
        } else {
          const countKey = resolveActiveColorCountKey(side);
          const cached = sideColorCountJsonCache[side.sideKey]?.[countKey];
          if (cached) canvasMap[side.sideKey] = cached;
        }
      }
      const jsonObjects = btoa(JSON.stringify(canvasMap));

      const activeSide = sides.find(s => s.sideKey === activeSideKey) ?? sides[0];
      const thumbnailDataUrl = await buildTemplateThumbnailDataUrl(activeSide, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
      const thumbnailBlob = await (await fetch(thumbnailDataUrl)).blob();

      const fd = new FormData();
      fd.set('templateTitle', templateTitle.trim());
      fd.set('productId', productConfig.productId);
      fd.set('jsonObjects', jsonObjects);
      fd.set('visible', '1');
      fd.set('thumbnail', thumbnailBlob, 'thumbnail.png');

      const token = safeGetItem('session', 'customedge_token');
      const res = await fetch('/api/admin/templates', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      setShowTemplateModal(false);
      setTemplateTitle('');
      showToast(t('designer.templateSaved'), 'success');
    } catch (err: any) {
      setTemplateError(err.message ?? t('designer.error.templateSaveFailed'));
    } finally {
      setSavingTemplate(false);
    }
  };

  // Composites each side's design onto its full product photo (at the design area's
  // real saved position, not the viewport's zoomed-down CSS position) and asks the
  // backend for the requested format — no compositing of its own needed here since
  // each data URL handed over is already the final, full-resolution page image.
  // 'pdf' is always multi-page; 'png'/'jpg' fall back to the backend's existing (but,
  // until now, never wired up) ZIP-of-all-sides path when there's more than one side,
  // since a single raster image can't hold every side at full resolution.
  const handleDownload = async (format: 'pdf' | 'png' | 'jpg') => {
    if (!sides.length) return;
    setShowDownloadMenu(false);
    setDownloading(true);
    setDownloadError(null);

    try {
      const dataUrl = await buildDownloadPdfDataUrls(sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
      const isZip = format !== 'pdf' && sides.length > 1;

      const res = await fetch('/api/download', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataUrl,
          outputType: format,
          product_id: productConfig?.productId,
          ...(isZip ? { previewImages: 1 } : {}),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      // The ZIP path streams a raw binary body, not the {url} JSON shape the
      // PDF/single-image paths return — needs a blob object URL instead.
      const href = isZip
        ? URL.createObjectURL(await res.blob())
        : (await res.json()).url;
      if (!href) return;

      const ext = isZip ? 'zip' : format;
      const a = document.createElement('a');
      a.href = href;
      a.download = `${productConfig?.productId ?? 'design'}.${ext}`;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (isZip) setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (err: any) {
      setDownloadError(err.message ?? t('designer.error.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  // Builds a link that reopens this exact design (not just the blank product
  // page) — `useSharedDesignLoader` on the receiving end reads `?design=`,
  // fetches it from the already-public GET /api/designs/:id (no ownership
  // check on read, only on delete/copy) and restores it into the canvas.
  // Reuses `savedDesignId` when the user already saved in this session so
  // clicking Share right after Save doesn't create a second, orphaned copy of
  // the same design — only saves fresh if nothing's been saved yet.
  const buildShareUrl = async (): Promise<string> => {
    const designId = savedDesignId ?? (await saveCurrentDesign()).designId;
    const url = new URL(window.location.href);
    url.searchParams.set('design', designId);
    return url.toString();
  };

  const handleShareAction = async (action: 'whatsapp' | 'email' | 'copy') => {
    setShowShareMenu(false);
    setSharing(true);
    setSaveError(null);
    try {
      const shareUrl = await buildShareUrl();
      if (action === 'copy') {
        await navigator.clipboard.writeText(shareUrl);
        showToast(t('designer.shareLinkCopied'), 'success');
      } else if (action === 'whatsapp') {
        const text = t('designer.shareMessage', { url: shareUrl });
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      } else {
        const subject = t('designer.shareEmailSubject');
        const body = t('designer.shareMessage', { url: shareUrl });
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
    } catch (err: any) {
      setSaveError(err.message ?? t('designer.error.shareFailed'));
    } finally {
      setSharing(false);
    }
  };

  const handleClose = () => notifyParent('DESIGNER_CLOSED');

  return (
    <header className={`designer-responsive-header${mobileMenuOpen ? ' designer-mobile-menu-open' : ''}`} style={{
      height: 52,
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      display: 'grid',
      // Center track was a bare `auto` — a grid item's implicit min-width is
      // its content's max-content size, NOT 0, so `auto` refused to shrink
      // below "every pill's natural width" even with maxWidth/overflow set
      // on the pills themselves, forcing the two `1fr` side tracks to give
      // up space and visually overlap the center column's text at narrower
      // viewports. `minmax(0, auto)` overrides that implicit minimum so the
      // center column can actually compress down to its `overflow: hidden`
      // content once the pills' own ellipsis-truncation kicks in.
      // gridTemplateColumns: '1fr minmax(0, auto) 1fr',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      columnGap: 12,
      alignItems: 'center',
      padding: '0 16px',
      flexShrink: 0,
    }}>
      <button
        type="button"
        className="designer-mobile-menu-toggle"
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
        aria-controls="designer-mobile-menu"
        onClick={() => setMobileMenuOpen(open => !open)}
      >
        {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
      {/* Left: brand mark + Design / Preview tabs */}
      <div className="designer-header-tabs" style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0, overflow: 'hidden' }}>
        {/* Merchant logo (customedge_connector/general/logo in Magento admin),
            passed through as ?logo= on the iframe URL. Sits before the Design
            tab so the tool reads as the store's own editor rather than a
            third-party embed. Falls back to a "CustomEdge" text mark when no
            logo is configured or the image fails to load. When the admin also
            sets a "Designer Logo Link" (?logoLink=), the logo becomes a real
            <a>, same convention as a normal site header's clickable logo —
            the text fallback is never a link, since "CustomEdge" isn't the
            merchant's own brand. */}
        <div style={{ display: 'flex', alignItems: 'center', paddingRight: 16, marginRight: 8, borderRight: '1px solid #e2e8f0', height: 28 }}>
          {brandLogoUrl && !logoFailed ? (
            brandLogoLink ? (
              <a href={brandLogoLink} style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src={brandLogoUrl}
                  alt="Store logo"
                  onError={() => setLogoFailed(true)}
                  style={{ maxHeight: 28, maxWidth: 140, objectFit: 'contain' }}
                />
              </a>
            ) : (
              <img
                src={brandLogoUrl}
                alt="Store logo"
                onError={() => setLogoFailed(true)}
                style={{ maxHeight: 28, maxWidth: 140, objectFit: 'contain' }}
              />
            )
          ) : (
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', letterSpacing: '-0.01em' }}>
              CustomEdge
            </span>
          )}
        </div>

        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => { onTabChange(tab); setMobileMenuOpen(false); }}
            style={{
              padding: '0 18px',
              height: 52,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              color: activeTab === tab ? 'var(--selection-text)' : '#64748b',
              borderBottom: activeTab === tab ? '2px solid var(--selection)' : '2px solid transparent',
              transition: 'color 0.15s, border-color 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {tab === 'design' ? designLabel : tab === 'preview' ? previewLabel : t('designer.tabs.preview3d')}
          </button>
        ))}
      </div>

      <div id="designer-mobile-menu" className="designer-header-menu" aria-hidden={undefined}>
      {/* Center: three independent selectors — Variant (Product color) /
          Print method / Print colors — each opens ProductOptionsModal. True
          grid-centered column (not just "next to the logo" inside the left
          flex group) so it stays centered in the header regardless of how
          wide the left tabs or right actions groups are. Each selector shows
          its "Select X" placeholder until a value is chosen, then swaps to
          the chosen value — and only renders at all if that option type is
          actually configured for this product (e.g. a product with no color
          variants shows just Print method + Print colors). */}
      <div className="designer-header-selectors" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
        {(() => {
          // Only shows a value once the customer has actually chosen one via
          // ProductOptionsModal — useProductSetup.ts's auto-selected first
          // variant (so the canvas isn't blank) does NOT count, otherwise
          // this pill could never show its "Select Variant" placeholder for
          // any product that has color variants configured.
          const activeColorVariant = hasSelectedColorVariant
            ? colorVariants.find(cv => cv.id === activeColorVariantId)
            : undefined;
          const activeColorLabel = activeColorVariant ? capitalizeLabel(activeColorVariant.colorName) : undefined;

          // For a product with print positions configured, ProductOptionsModal
          // writes the customer's method/color choice into
          // selectedPrintMethodByPosition/selectedPrintColorByPosition (keyed
          // per position), NOT the flat selectedPrintMethod/
          // selectedPrintColorOption — those stay null for these products (see
          // designStore.ts's own field comments). Reading only the flat fields
          // here left these two pills stuck on their placeholder forever for
          // any position-based product (e.g. PX120181), even right after the
          // customer picked and applied a method/color per position. Mirrors
          // the printPosition pill's own join(', ') below: one method/color
          // per selected position, deduped when every position agrees so a
          // single shared "for all" pick still reads as one clean value
          // instead of "Zeefdruk, Zeefdruk".
          // Each raw method/color value is resolved through the Magento-
          // translated maps before display (e.g. "Zeefdruk"+2 -> "2 kleuren"
          // instead of a bare "2") — translatedMethodLabel/
          // translatedColorOptionLabel fall back to the raw value unchanged
          // when no translation row exists, so this is a no-op for any
          // product without synced translation data.
          //
          // Falls back to the flat selectedPrintMethod/selectedPrintColorOption
          // for any position with no per-position entry of its own — mirrors
          // useProductSetup.ts's own effectiveMethodFor() exactly (used there
          // for side/area filtering), which already documents WHY: Magento's
          // real PDP button sends the singular `&printMethod=`/`&printColor=`
          // params (not the by-position variants) for many product shapes,
          // even when `&printPositions=` is also present. Found + fixed
          // 2026-08-05: without this fallback, that exact real-world param
          // combination left these two pills stuck on their placeholder
          // forever even though the canvas/area label correctly showed the
          // resolved method (via effectiveMethodFor's own fallback) — a
          // pill-only bug, not a data or canvas bug.
          const rawMethodForPosition = (p: string) => selectedPrintMethodByPosition[p] ?? selectedPrintMethod ?? undefined;
          const rawColorForPosition = (p: string) => selectedPrintColorByPosition[p] ?? selectedPrintColorOption ?? undefined;
          const printMethodLabel = selectedPrintPositions.length > 0
            ? Array.from(new Set(selectedPrintPositions.map(p => rawMethodForPosition(p)).filter((m): m is string => !!m)))
                .map(m => translatedMethodLabel(productConfig, m) ?? m)
                .join(', ') || undefined
            : (selectedPrintMethod ? (translatedMethodLabel(productConfig, selectedPrintMethod) ?? selectedPrintMethod) : undefined);
          const printColorLabel = selectedPrintPositions.length > 0
            ? Array.from(new Set(
                selectedPrintPositions
                  .filter(p => rawColorForPosition(p) != null)
                  .map(p => translatedColorOptionLabel(productConfig, rawMethodForPosition(p), rawColorForPosition(p)!, t('designer.options.fullColor')))
              )).join(', ') || undefined
            : (selectedPrintColorOption == null
                ? undefined
                : translatedColorOptionLabel(productConfig, selectedPrintMethod, selectedPrintColorOption, t('designer.options.fullColor')));

          // Fixed height/max-width so a value with many joined parts (e.g. a
          // print-position pill after selecting every position: "Voorzijde,
          // Achterzijde, Borst, Arm rechts, Arm links") can't grow the pill
          // into a multi-line block that pushes/overlaps the Design/Preview
          // tabs to its left and the Save/Share/Download actions to its
          // right — reported live as the header "look[ing] ugly when select
          // multiple options". The value text itself is truncated with an
          // ellipsis (see the `s.value` span below) rather than the pill
          // silently clipping it with `overflow: hidden` alone, so it's
          // still legible as "many things selected, hover/click for the
          // full list" instead of an abrupt hard cut mid-word.
          const selectorStyle: React.CSSProperties = {
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '0 10px', height: 32, background: 'none',
            border: '1px solid #e2e8f0', borderRadius: 16,
            cursor: 'pointer', fontSize: 13,
            maxWidth: 160, minWidth: 0, flexShrink: 1,
          };

          const selectors: Array<{ key: string; label: string; value?: string; swatch?: { url?: string; hex?: string } }> = [
            ...(colorVariants.length > 0
              ? [{
                  key: 'variant', label: t('designer.options.selectVariant'), value: activeColorLabel,
                  swatch: activeColorVariant ? { url: activeColorVariant.swatchImageUrl, hex: activeColorVariant.colorHex } : undefined,
                }]
              : []),
            ...((productConfig?.printPositions?.length ?? 0) > 0
              ? [{ key: 'printPosition', label: t('designer.options.selectPrintPosition'), value: selectedPrintPositions.length ? selectedPrintPositions.map(p => translatedPositionLabel(productConfig, p) ?? p).join(', ') : undefined }]
              : []),
            ...((productConfig?.printMethods?.length ?? 0) > 0
              ? [{ key: 'printMethod', label: t('designer.options.selectPrintMethod'), value: printMethodLabel }]
              : []),
            ...((productConfig?.printColorOptions?.length ?? 0) > 0
              ? [{ key: 'printColor', label: t('designer.options.selectPrintColor'), value: printColorLabel }]
              : []),
          ];

          return selectors.map(s => (
            <button
              key={s.key}
              title={s.value ?? s.label}
              onClick={() => setShowOptionsModal(true)}
              onMouseEnter={() => s.swatch && setHoveredSelectorKey(s.key)}
              onMouseLeave={() => s.swatch && setHoveredSelectorKey(null)}
              style={{ ...selectorStyle, position: 'relative' }}
            >
              {s.swatch && (
                s.swatch.url ? (
                  <img
                    src={s.swatch.url}
                    alt=""
                    // See crossOriginImage.ts — this swatch photo can be the exact
                    // same S3 object as the active side's own imagePath (a product
                    // with one photo per color, reused as both), so it must resolve
                    // crossOrigin identically or risk the documented CORS cache-race.
                    crossOrigin={crossOriginForUrl(s.swatch.url)}
                    style={{ width: 20, height: 20, objectFit: 'cover', borderRadius: '50%', border: '1px solid #e2e8f0', flexShrink: 0 }}
                  />
                ) : (
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    background: s.swatch.hex ?? '#e2e8f0', border: '1px solid #e2e8f0',
                  }} />
                )
              )}
              {s.value ? (
                <span style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{s.value}</span>
              ) : (
                <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{s.label}</span>
              )}
              <span style={{ color: '#94a3b8', fontSize: 11, flexShrink: 0 }}>▾</span>

              {/* Hover popover: 200x200 preview of the selected variant's swatch
                  image, shown above the pill so it doesn't get clipped by the
                  header's own height. Only wired for the variant selector since
                  it's the only one with an actual image (print method / print
                  color are plain labels). */}
              {s.swatch && hoveredSelectorKey === s.key && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)',
                    zIndex: 100, width: 200, height: 200, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                    cursor: 'default',
                  }}
                >
                  {s.swatch.url ? (
                    <img
                      src={s.swatch.url}
                      alt=""
                      crossOrigin={crossOriginForUrl(s.swatch.url)}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: s.swatch.hex ?? '#e2e8f0' }} />
                  )}
                </div>
              )}
            </button>
          ));
        })()}
      </div>

      {/* Right: actions */}
      <div className="designer-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', justifySelf: 'end' }}>
        {saveError && <span style={{ color: '#ef4444', fontSize: 12 }}>{saveError}</span>}
        {downloadError && <span style={{ color: '#ef4444', fontSize: 12 }}>{downloadError}</span>}
        {isFinishStep && finishError && <span style={{ color: '#ef4444', fontSize: 12 }}>{finishError}</span>}

        <LanguageSwitcher />

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-outline"
        >
          {saving ? t('designer.saving') : t('designer.save')}
        </button>

        {isStaffAdmin && (
          <button
            onClick={() => { setTemplateError(null); setShowTemplateModal(true); }}
            className="btn-outline"
          >
            {t('designer.saveAsTemplate')}
          </button>
        )}

        {/* Share: WhatsApp / Email / Copy link — all three send the same
            `?design=<id>` URL built by buildShareUrl(), auto-saving first if
            nothing's been saved yet in this session. */}
        <div style={{ position: 'relative', display: 'flex' }}>
          <button
            onClick={() => setShowShareMenu(v => !v)}
            disabled={sharing}
            className="btn-outline"
          >
            {sharing ? t('designer.sharing') : t('designer.share')}
          </button>
          {showShareMenu && !sharing && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setShowShareMenu(false)} />
              <div className="designer-download-formats" style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 99,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '4px 0', minWidth: 170,
              }}>
                {([
                  { action: 'whatsapp' as const, label: t('designer.shareWhatsapp') },
                  { action: 'email' as const,    label: t('designer.shareEmail') },
                  { action: 'copy' as const,     label: t('designer.shareCopyLink') },
                ]).map(({ action, label }) => (
                  <button
                    key={action}
                    onClick={() => handleShareAction(action)}
                    style={{
                      display: 'block', width: '100%', padding: '6px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      textAlign: 'left', fontSize: 12, color: '#374151',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Split button: the primary "Download PDF" action is unchanged (same
            label/behaviour the existing permanent e2e test clicks directly) —
            the small caret next to it opens a menu for the other formats. A
            small gap (not flush) keeps the caret reading as a distinct
            control now that the main button is a closed pill shape, rather
            than looking like a stray character stuck to its right edge. */}
        <div className="designer-download-control" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            onClick={() => { if (window.matchMedia('(max-width: 767px)').matches) { setShowDownloadMenu(false); setShowMobileDownloadModal(true); } else { handleDownload('pdf'); } }}
            disabled={downloading}
            className="btn-outline"
          >
            {downloading ? t('designer.downloadingPdf') : t('designer.downloadPdf')}
          </button>
          <button
            onClick={() => { if (window.matchMedia('(max-width: 767px)').matches) { setShowDownloadMenu(false); setShowMobileDownloadModal(true); } else { setShowDownloadMenu(v => !v); } }}
            disabled={downloading}
            title={t('designer.moreFormats')}
            style={{
              padding: '0 4px',
              height: 32,
              background: 'none',
              border: 'none',
              cursor: downloading ? 'not-allowed' : 'pointer',
              fontSize: 12,
              color: downloading ? '#94a3b8' : '#111',
            }}
          >
            ▾
          </button>
          {showDownloadMenu && !downloading && (
            <>
              <div className="designer-download-backdrop" style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setShowDownloadMenu(false)} />
              <div style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 99,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '4px 0', minWidth: 120,
              }}>
                {([
                  { format: 'png' as const, label: t('designer.downloadAs', { format: 'PNG' }) },
                  { format: 'jpg' as const, label: t('designer.downloadAs', { format: 'JPG' }) },
                ]).map(({ format, label }) => (
                  <button
                    key={format}
                    onClick={() => handleDownload(format)}
                    style={{
                      display: 'block', width: '100%', padding: '6px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      textAlign: 'left', fontSize: 12, color: '#374151',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {isFinishStep && finishDone ? (
          <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', padding: '0 6px' }}>{t('cart.added')}</span>
        ) : (
          <button
            key={isFinishStep ? 'finish' : 'next'}
            onClick={isFinishStep ? finishDesign : onCartClick}
            disabled={isFinishStep ? finishLoading : !onCartClick}
            className={isFinishStep ? 'btn btn-primary finish-design-pulse' : 'btn btn-primary'}
            style={{
              padding: '0 18px',
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              opacity: isFinishStep && finishLoading ? 0.7 : 1,
            }}
          >
            {isFinishStep ? (finishLoading ? t('cart.adding') : t('cart.title')) : t('designer.next')}
            {/* Arrow as its own icon, not baked into the translated string —
                a raw "→" glyph inline with Latin text sits on a different
                vertical metric in most fonts (confirmed in Poppins), so
                flex's alignItems:center on the whole text node couldn't
                actually center the arrow relative to the letters. An icon
                component's own box aligns cleanly instead. Omitted on the
                Finish Design step — that action ends the flow, it doesn't
                advance to something else. */}
            {!isFinishStep && <ArrowRight size={16} strokeWidth={2.25} style={{ display: 'block' }} />}
          </button>
        )}

        {isPopupMode && (
          <button
            onClick={handleClose}
            style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, padding: '0 4px' }}
            title={t('designer.close')}
          >×</button>
        )}
      </div>

      </div>
      {showTemplateModal && (
        <div
          onClick={() => !savingTemplate && setShowTemplateModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, padding: 20, width: 360, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px', color: '#1e293b' }}>
              {t('designer.saveAsTemplate')}
            </h3>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              {t('designer.templateTitle')}
            </label>
            <input
              autoFocus
              value={templateTitle}
              onChange={e => setTemplateTitle(e.target.value)}
              disabled={savingTemplate}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
            />
            {templateError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{templateError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button
                onClick={() => setShowTemplateModal(false)}
                disabled={savingTemplate}
                style={{ padding: '6px 14px', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="btn btn-primary"
                style={{ padding: '6px 14px', cursor: savingTemplate ? 'not-allowed' : 'pointer', fontSize: 13 }}
              >
                {savingTemplate ? t('designer.savingTemplate') : t('designer.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMobileDownloadModal && (
        <div className="designer-mobile-download-overlay" role="presentation">
          <div className="designer-mobile-download-dialog" role="dialog" aria-modal="true" aria-label={t('designer.downloadPdf')}>
            <div className="designer-mobile-download-heading">
              <strong>{t('designer.downloadPdf')}</strong>
              <button type="button" aria-label="Close download options" onClick={() => setShowMobileDownloadModal(false)}>×</button>
            </div>
            {(['pdf', 'png', 'jpg'] as const).map(format => (
              <button key={format} type="button" disabled={downloading} onClick={() => { setShowMobileDownloadModal(false); void handleDownload(format); }}>
                {format === 'pdf' ? t('designer.downloadPdf') : t('designer.downloadAs', { format: format.toUpperCase() })}
              </button>
            ))}
          </div>
        </div>
      )}
      {showOptionsModal && <ProductOptionsModal onClose={() => setShowOptionsModal(false)} />}
    </header>
  );
}
